import { Book } from '../../store/books/types'
import { OrderEntryModel } from '../../store/depthOfMarket/types'
import {
  OrderType,
  TobParameters,
  ValidationResult
} from '../../store/order/types'
import { StagedOrder } from '../../store/stagedOrders/types'
import { emptyTob } from '../DepthOfMarket/helpers'
import {
  EnhancedOrderUploadRow,
  IcebergSize,
  UploadTob
} from './EnhancedUpload/types'

export const invalidDataToCopy = (data: ValidationResult[]) => {
  return data
    .map((row) =>
      [row.isin, row.orderType === 'buy' ? 'Bid' : 'Ofr', row.error ?? ''].join(
        '\t'
      )
    )
    .join('\n')
}
export const createUploadOrder = <T extends OrderType>(
  type: T,
  isIceberg: boolean,
  isTob: boolean
) => ({
  type,
  size: isIceberg ? { displaySize: '', totalSize: '' } : '',
  priceAmount: isTob ? { limit: '', floor: '' } : '',
  spreadAmount: isTob ? { limit: '', floor: '' } : ''
})
export const createNewRow = (
  index: number,
  identifier: string,
  isIceberg: boolean,
  isTob: boolean = false
): EnhancedOrderUploadRow => {
  return {
    index,
    identifier,
    buyDetails: createUploadOrder('buy', isIceberg, isTob),
    sellDetails: createUploadOrder('sell', isIceberg, isTob)
  }
}

const isEmptySize = (size: string | IcebergSize) => {
  if (typeof size === 'string') {
    return !parseInt(size, 10)
  }
  return !parseInt(size.displaySize, 10) && !parseInt(size.totalSize, 10)
}

const isEmptyAmount = (amount: string | UploadTob) => {
  if (typeof amount === 'string') {
    return !amount
  }
  // TODO actual TOB logic
  return true
}

type DetailType =
  | EnhancedOrderUploadRow['buyDetails']
  | EnhancedOrderUploadRow['sellDetails']

const hasBothPriceAndSpread = (order: DetailType) => {
  return !isEmptyAmount(order.priceAmount) && !isEmptyAmount(order.spreadAmount)
}

const hasPriceOrSpread = (order: DetailType) => {
  return !isEmptyAmount(order.priceAmount) || !isEmptyAmount(order.spreadAmount)
}

const hasInvalidSpread = (order: DetailType) => {
  if (typeof order.spreadAmount === 'string') {
    if (!order.spreadAmount) return false
    const spreadFormat = /^[+-]?\d+\.?\d*$/
    return !spreadFormat.test(order.spreadAmount)
  }
  // TODO: TOB
  return false
}

const hasInvalidPrice = (order: DetailType) => {
  if (typeof order.priceAmount === 'string') {
    if (!order.priceAmount) return false
    const priceFormat = /^[$£]?\d+\.?\d*$/
    return !priceFormat.test(order.priceAmount)
  }
  // TODO: TOB
  return false
}

export const addErrorMessage = (
  row: EnhancedOrderUploadRow
): EnhancedOrderUploadRow => {
  if (
    !row.identifier &&
    (hasPriceOrSpread(row.buyDetails) || hasPriceOrSpread(row.sellDetails))
  ) {
    return { ...row, errMsg: 'Please enter an identifier.' }
  }
  const digits = /\d+/
  const isTicker = !digits.test(row.identifier ?? '')
  if (
    isTicker &&
    (hasPriceOrSpread(row.buyDetails) || hasPriceOrSpread(row.sellDetails))
  ) {
    return { ...row, errMsg: "Can't create orders for tickers." }
  }

  if (
    (!isEmptySize(row.buyDetails.size) && !hasPriceOrSpread(row.buyDetails)) ||
    (!isEmptySize(row.sellDetails.size) && !hasPriceOrSpread(row.sellDetails))
  ) {
    return { ...row, errMsg: 'Please enter a price or spread.' }
  }
  if (hasInvalidSpread(row.buyDetails) || hasInvalidSpread(row.sellDetails)) {
    return { ...row, errMsg: 'Invalid characters in spread.' }
  }
  if (hasInvalidPrice(row.buyDetails) || hasInvalidPrice(row.sellDetails)) {
    return { ...row, errMsg: 'Invalid characters in price.' }
  }
  if (
    hasBothPriceAndSpread(row.buyDetails) ||
    hasBothPriceAndSpread(row.sellDetails)
  ) {
    return { ...row, errMsg: 'Please enter price OR spread (not both).' }
  }
  if (
    (hasPriceOrSpread(row.buyDetails) && isEmptySize(row.buyDetails.size)) ||
    (hasPriceOrSpread(row.sellDetails) && isEmptySize(row.sellDetails.size))
  ) {
    return { ...row, errMsg: 'Please enter an amount.' }
  }
  if (row.bookId === -99) {
    return { ...row, errMsg: 'Book not found.' }
  }
  return { ...row, errMsg: '' }
}
export const createGridRows = (
  identifiers: string[],
  isIceberg: boolean,
  isTob: boolean = false
): EnhancedOrderUploadRow[] => {
  return [...identifiers, ''].map((id, i) =>
    createNewRow(i, id, isIceberg, isTob)
  )
}

export const dateFormat = 'M/DD/YY, h:mm a'

const sizeToArr = (size: string | IcebergSize) => {
  if (typeof size === 'string') return [size]
  return [size.displaySize, size.totalSize]
}
export const copyRow = (row: EnhancedOrderUploadRow, books: Book[]) => {
  const selectedBook = books.find((book) => book.id === row.bookId)
  return [
    row.identifier,
    ...sizeToArr(row.buyDetails.size).reverse(),
    row.buyDetails.priceAmount,
    row.buyDetails.spreadAmount,
    row.sellDetails.spreadAmount,
    row.sellDetails.priceAmount,
    ...sizeToArr(row.sellDetails.size),
    selectedBook?.name ?? ''
  ].join('\t')
}

const getAmount = (amount: string | UploadTob) => {
  if (typeof amount === 'string') {
    return amount
  }
  return {
    tob: true,
    limitPrice: parseInt(amount.limit, 10),
    floorPrice: parseInt(amount.floor, 10)
  } as TobParameters
}

/*  TODO: I thought we would use this for the actual uploads, because
    I thought we would be doing validations prior to upload.
    We may still decide to do that, but the old logic didn't.
 */
const createOrderEntryModelFromUploadOrder = (
  {
    type,
    size,
    priceAmount,
    spreadAmount
  }:
    | EnhancedOrderUploadRow['buyDetails']
    | EnhancedOrderUploadRow['sellDetails'],
  selectedBook?: number
) => {
  const sizes = sizeToArr(size)
  // if they only provided one value, it's totalsize
  const totalSize = (sizes.length === 1 ? sizes[0] : sizes[1]) || ''
  const displaySize = sizes.length === 1 ? '' : sizes[0] || ''
  const amountType = !isEmptyAmount(priceAmount) ? 'price' : 'spread'
  const amount =
    amountType === 'price' ? getAmount(priceAmount) : getAmount(spreadAmount)
  const aon = sizes.some(
    (aonSize) => aonSize.endsWith('a') && parseInt(aonSize, 10)
  )
  return {
    type,
    amountType,
    displaySize,
    totalSize,
    minSize: '',
    amount,
    selectedBook,
    isLive: false,
    aon
  } as const
}

export const createOrderEntryModelsFromUploadRow = (
  row: EnhancedOrderUploadRow,
  bookId?: number
) => {
  const result: OrderEntryModel[] = []
  if (hasPriceOrSpread(row.buyDetails)) {
    result.push(createOrderEntryModelFromUploadOrder(row.buyDetails, bookId))
  }
  if (hasPriceOrSpread(row.sellDetails)) {
    result.push(createOrderEntryModelFromUploadOrder(row.sellDetails, bookId))
  }
  return result
}

const getStagedSalesAmount = (amount: string | UploadTob) => {
  if (typeof amount === 'string') {
    return parseFloat(amount)
  }
  return parseFloat(amount.floor)
}

const createStagedOrderFromUploadOrder = (
  securityId: number,
  {
    type,
    size,
    priceAmount,
    spreadAmount
  }:
    | EnhancedOrderUploadRow['buyDetails']
    | EnhancedOrderUploadRow['sellDetails'],
  selectedBook?: number
) => {
  // This is hackey--I hadn't realized we'd need to create staged orders from this when I started down this road
  const sizes = sizeToArr(size).map((s) => s.replace(',', ''))
  // if they only provided one value, it's displaySize
  const totalSize = parseInt(sizes[1], 10) || undefined
  const displaySize = parseInt(sizes[0], 10) || 0
  const isSpreadOrder = !isEmptyAmount(spreadAmount)
  const orderAmount = isSpreadOrder ? spreadAmount : priceAmount
  const isPeggedOrder = !(typeof orderAmount === 'string')
  const tob = isPeggedOrder
    ? (getAmount(orderAmount) as TobParameters)
    : emptyTob

  const price = getStagedSalesAmount(priceAmount) || undefined
  const spread = getStagedSalesAmount(spreadAmount) || undefined
  const allOrNone = sizes.some(
    (aonSize) => aonSize.endsWith('a') && parseInt(aonSize, 10)
  )
  const result: StagedOrder = {
    orderType: type,
    securityId,
    price,
    spread,
    isSpreadOrder,
    size: displaySize,
    totalSize,
    allOrNone,
    individualMin: 0,
    custId: selectedBook ?? 0,
    tob
  } as const
  return result
}
export const createStagedOrdersFromUploadRow = (
  securityId: number,
  row: EnhancedOrderUploadRow,
  bookId?: number
) => {
  const result: StagedOrder[] = []
  if (hasPriceOrSpread(row.buyDetails)) {
    result.push(
      createStagedOrderFromUploadOrder(
        securityId,
        row.buyDetails,
        row.bookId ?? bookId
      )
    )
  }
  if (hasPriceOrSpread(row.sellDetails)) {
    result.push(
      createStagedOrderFromUploadOrder(
        securityId,
        row.sellDetails,
        row.bookId ?? bookId
      )
    )
  }
  return result
}
